/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Aufgabe } from '../models/Aufgabe';
import type { AufgabePromptDaten } from '../models/AufgabePromptDaten';
import type { Prompt } from '../models/Prompt';
import type { PromptDaten } from '../models/PromptDaten';
import type { PromptListe } from '../models/PromptListe';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class PromptsService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Prompt-Versionen einer Aufgabe
   * Liefert alle Prompt-Versionen einer Aufgabe, sortiert nach Erstellzeitpunkt absteigend (neueste zuerst).
   * @returns PromptListe Liste aller Prompt-Versionen für die Aufgabe
   * @throws ApiError
   */
  public getPromptsByAufgabeId({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<PromptListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/prompts',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Neue Prompt-Version anlegen
   * Erstellt eine neue Prompt-Version für die Aufgabe. `prevVersion` der neuen Version
   * wird serverseitig auf die aktuell aktive Prompt-Version gesetzt. Standardmäßig wird
   * die neue Version anschließend als aktive Version der Aufgabe übernommen
   * (kann mit `?activate=false` deaktiviert werden).
   *
   * @returns Prompt Die neu erstellte Prompt-Version
   * @throws ApiError
   */
  public createAufgabePrompt({
    aufgabeId,
    requestBody,
    activate = true,
  }: {
    aufgabeId: string,
    requestBody: AufgabePromptDaten,
    /**
     * Wenn true (Standard), wird die neue Version direkt als aktive Version der Aufgabe gesetzt.
     */
    activate?: boolean,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/prompts',
      path: {
        'aufgabeId': aufgabeId,
      },
      query: {
        'activate': activate,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Prompt-Version einer Aufgabe löschen
   * Löscht (soft-delete) eine Prompt-Version einer Aufgabe. Die aktuell aktive
   * Prompt-Version kann nicht gelöscht werden — in diesem Fall wird ein Fehler
   * zurückgegeben.
   *
   * @returns Prompt Die gelöschte Prompt-Version
   * @throws ApiError
   */
  public deleteAufgabePrompt({
    aufgabeId,
    promptId,
  }: {
    aufgabeId: string,
    promptId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}/prompts/{promptId}',
      path: {
        'aufgabeId': aufgabeId,
        'promptId': promptId,
      },
      errors: {
        400: `Die Prompt-Version ist die aktive Version der Aufgabe und kann nicht gelöscht werden.`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktive Prompt-Version einer Aufgabe setzen
   * Setzt eine andere Prompt-Version als aktive Version der Aufgabe. Die Prompt-Version
   * muss bereits zur Aufgabe gehören (über `prompt_relation`).
   *
   * @returns Aufgabe Die aktualisierte Aufgabe
   * @throws ApiError
   */
  public setActiveAufgabePrompt({
    aufgabeId,
    promptId,
  }: {
    aufgabeId: string,
    promptId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/aufgaben/{aufgabeId}/active-prompt/{promptId}',
      path: {
        'aufgabeId': aufgabeId,
        'promptId': promptId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktive Prompt-Version einer Aufgabe
   * Liefert die aktuell aktive Prompt-Version einer Aufgabe.
   * @returns Prompt Die aktuell aktive Prompt-Version
   * @throws ApiError
   */
  public getActivePromptByAufgabeId({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/prompts/active',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Inbound-Prompt-Versionen einer Aufgabe
   * Liefert alle Inbound-Prompt-Versionen einer Aufgabe, sortiert nach Erstellzeitpunkt absteigend.
   * @returns PromptListe Liste der Inbound-Prompt-Versionen
   * @throws ApiError
   */
  public getPromptsByAufgabeInbound({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<PromptListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/inbound-prompts',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Inbound-Prompt-Version einer Aufgabe erstellen
   * Erstellt eine neue Inbound-Prompt-Version für eine Aufgabe.
   * @returns Prompt Die erstellte Inbound-Prompt-Version
   * @throws ApiError
   */
  public createAufgabeInboundPrompt({
    aufgabeId,
    requestBody,
    activate = false,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
    requestBody: AufgabePromptDaten,
    /**
     * Wenn true, wird die neue Inbound-Prompt-Version direkt als aktive Version gesetzt
     */
    activate?: boolean,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/inbound-prompts',
      path: {
        'aufgabeId': aufgabeId,
      },
      query: {
        'activate': activate,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Inbound-Prompt-Version einer Aufgabe löschen
   * Löscht (soft-delete) eine Inbound-Prompt-Version. Die aktive Version kann nicht gelöscht werden.
   * @returns Prompt Die gelöschte Inbound-Prompt-Version
   * @throws ApiError
   */
  public deleteAufgabeInboundPrompt({
    aufgabeId,
    promptId,
  }: {
    aufgabeId: string,
    promptId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}/inbound-prompts/{promptId}',
      path: {
        'aufgabeId': aufgabeId,
        'promptId': promptId,
      },
      errors: {
        400: `Bad Request (z.B. aktive Version)`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktive Inbound-Prompt-Version setzen
   * Setzt eine Inbound-Prompt-Version als aktive Version für eine Aufgabe.
   * @returns Aufgabe Die aktualisierte Aufgabe
   * @throws ApiError
   */
  public setActiveAufgabeInboundPrompt({
    aufgabeId,
    promptId,
  }: {
    aufgabeId: string,
    promptId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/aufgaben/{aufgabeId}/inbound-prompts/active',
      path: {
        'aufgabeId': aufgabeId,
      },
      query: {
        'promptId': promptId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Eine Prompt-Version bekommen
   * Liefert eine einzelne Prompt-Version anhand ihrer ID.
   * @returns Prompt Die Prompt-Version
   * @throws ApiError
   */
  public getPrompt({
    promptId,
  }: {
    /**
     * Eindeutige ID der Prompt-Version
     */
    promptId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/prompts/{promptId}',
      path: {
        'promptId': promptId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Eine Prompt-Version aktualisieren
   * Aktualisiert den Inhalt einer bestehenden Prompt-Version. Die Versionierungs-Kette
   * (`prevVersion`) und die Stage bleiben unverändert. Der Besitzer (z. B. Aufgabe oder
   * Telefonnummer) wird serverseitig über die `prompt_relation` aufgelöst, um die
   * Berechtigungsprüfung durchzuführen.
   *
   * @returns Prompt Die aktualisierte Prompt-Version
   * @throws ApiError
   */
  public updatePrompt({
    promptId,
    requestBody,
  }: {
    promptId: string,
    requestBody: PromptDaten,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/prompts/{promptId}',
      path: {
        'promptId': promptId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Eine Prompt-Version löschen
   * Löscht (soft-delete) eine Prompt-Version. Die aktuell aktive Version des Besitzers
   * (z. B. Aufgabe oder Telefonnummer) kann nicht gelöscht werden — in diesem Fall wird
   * ein Fehler zurückgegeben. Der Besitzer wird serverseitig über die `prompt_relation`
   * aufgelöst, um die Berechtigungsprüfung durchzuführen.
   *
   * @returns Prompt Die gelöschte Prompt-Version
   * @throws ApiError
   */
  public deletePrompt({
    promptId,
  }: {
    promptId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/prompts/{promptId}',
      path: {
        'promptId': promptId,
      },
      errors: {
        400: `Die Prompt-Version ist die aktive Version des Besitzers und kann nicht gelöscht werden.`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

}
